import { differenceInHours } from "date-fns";
import { UserAccount } from "src/types/User";
import isInCustody from "./isInCustody";

const IN_CUSTODY_REVIEW_HOURS = 24;

/**
 * Checks if a user is in custody (pre-release) and has recently completed their application.
 * Used to show the review screen for pre-release participants.
 *
 * @param user - The user account to check
 * @returns true if user is in custody and application was completed within 24 hours
 */
export default function isRecentInCustodyApplication(
  user: UserAccount
): boolean {
  if (!isInCustody(user.incarcerationStatus)) {
    return false;
  }

  // Must have completed application
  if (!user.application) {
    return false;
  }

  // Check when application was created (or last updated as fallback)
  // New applications only have createdAt; lastUpdatedAt is added on updates
  const application = user.application as
    | { createdAt?: string; lastUpdatedAt?: string }
    | undefined;
  const applicationCompletedAt =
    application?.createdAt || application?.lastUpdatedAt;

  if (!applicationCompletedAt) {
    return false;
  }

  const completedTime = new Date(applicationCompletedAt);
  const now = new Date();
  const hoursSinceCompletion = differenceInHours(now, completedTime);

  // Return false for future timestamps (negative hours)
  if (hoursSinceCompletion < 0) {
    return false;
  }

  return hoursSinceCompletion < IN_CUSTODY_REVIEW_HOURS;
}
